import { posthogHeaders } from './analytics/client';
import { collabMaterialShared, type AnalyticsEvent, type InvitationEmailResult } from '../core/events';
import {
  formatOfContentType, previewContentType, previewKindOfContentType, sizeBucket,
} from '../core/materials';
import type { StandardsContext } from '../core/standards-context';
import type { Role } from '../core/types';

/**
 * The Worker's wire contract, in the camelCase the browser reads, and the one place a
 * request is made.
 *
 * Two things are load-bearing here rather than in a component:
 *
 * 1. **Every call carries the bearer token, downloads included.** A material is fetched with
 *    `Authorization` and handed to the teacher as a blob URL. A plain `<a href>` or a
 *    `window.open` sends no header and 401s, and the obvious repair — `?token=` — would put
 *    a Clerk JWT into Worker logs, referrers and PostHog's `$current_url`, which is the leak
 *    the rest of this unit works to prevent. **No material URL ever carries a token in its
 *    path or its query.**
 *
 * 2. **`preview_collab_material_shared` is emitted here and nowhere else** (KTD15, R19). It
 *    is browser-side because `growthActivity()` is consulted only inside the browser-side
 *    `track`; a server copy would never reach the growth metric, and no name may appear on
 *    both sides.
 */

// --- what the Worker returns ---------------------------------------------------------------

export interface Me {
  userId: string;
  email: string | null;
  emailVerified: boolean;
  subscriptionState: 'active' | 'none' | 'unknown';
}

export interface GroupSummary {
  id: string;
  name: string;
  createdAt: string;
  role: Role;
  memberCount: number;
  /**
   * How many of the group's visible materials have a comment this teacher has not read.
   * Optional because a group from an older Worker, or a test fixture, has none — read as 0.
   */
  unreadMaterials?: number;
  /**
   * The group's Standards check defaults. Absent when none is set, and from an older Worker
   * (KTD5), so a missing field reads as no default.
   */
  standardsDefaults?: StandardsContext;
}

export interface Member {
  userId: string;
  email: string;
  role: Role;
  joinedAt: string;
}

export interface PendingInvitation {
  id: string;
  email: string;
  invitedBy: string;
  createdAt: string;
  expiresAt: string;
  /** Why the last attempt on this link bounced — R10's recovery path, for the admin only. */
  lastRefusal: string | null;
  /** Set when the invitee declined. The row stays listed so the admin can see it and resend. */
  declinedAt: string | null;
}

export interface CreatedInvitation {
  id: string;
  email: string;
  createdAt: string;
  expiresAt: string;
  /** Carries the plaintext token in its fragment. Never logged, never captured, never stored. */
  link: string;
}

/**
 * What happened to the invitation's email. Every value but `sent` means the admin holds the
 * only copy of the link and has to pass it on: no key (`not_configured`), the kill switch
 * (`disabled`), or SendGrid refused (`failed`). The same union the Worker's event carries, so the two cannot drift.
 */
export type EmailResult = InvitationEmailResult;

/** One address's answer from the create and resend routes. */
export type InvitationOutcome =
  | { result: 'invited'; emailResult: EmailResult; invitation: CreatedInvitation }
  | { result: 'already_member'; email: string };

/** A live invitation addressed to the caller, for the groups-page banner. */
export interface MyInvitation {
  id: string;
  groupId: string;
  groupName: string;
  /** The inviting admin's roster address, or null when they have since left the group. */
  invitedByEmail: string | null;
  createdAt: string;
  expiresAt: string;
}

export interface MaterialSummary {
  id: string;
  title: string;
  note: string | null;
  filename: string;
  contentType: string;
  sizeBytes: number;
  uploadedBy: string;
  createdAt: string;
  /**
   * Whether the Worker holds a thumbnail for it. Optional because a material from an
   * older Worker, or a test fixture, has neither field, and both read as "not yet".
   */
  hasThumbnail?: boolean;
  /** Pages, as the uploader's browser counted them. For display only. */
  pageCount?: number | null;
  /** At least one comment on it this teacher has not read. Absent reads as read. */
  unread?: boolean;
  /** The fields a run set for this material, shared with the group. Absent reads as none. */
  standardsOverride?: StandardsContext;
}

export interface CommentView {
  id: string;
  parentId: string | null;
  authorUserId: string;
  /** An active member's address, or "Former member" (R18). Never a Clerk lookup. */
  author: string;
  /** Blank for a tombstone. The row is still here; the words are not. */
  body: string;
  /**
   * The passage it hangs from, in page coordinates — or `null` for a comment about the whole
   * material, which is still the ordinary case. Null for a tombstone too.
   *
   * Typed as `unknown` deliberately: this is a field off the wire, and the viewer decides
   * whether it is an anchor it can place by running `isHighlightAnchor` on it. Typing it as
   * `HighlightAnchor` here would be this file asserting something it has not checked.
   */
  anchor: unknown;
  createdAt: string;
  deletedAt: string | null;
  deleted: boolean;
}

export interface Thread {
  material: { id: string; title: string | null; deleted: boolean };
  comments: CommentView[];
}

// --- calling -------------------------------------------------------------------------------

export type ApiResult<T> =
  | { ok: true; data: T }
  | { ok: false; status: number; error: string; reason?: string };

/**
 * Every call carries the Clerk session token. `getToken` is passed in rather than imported
 * so components stay testable without mounting a `ClerkProvider`.
 */
export type GetToken = () => Promise<string | null>;

/** Status 0 is a network failure (below); 5xx is the Worker failing. Neither is a refusal. */
export const isUnreachable = (status: number): boolean => status === 0 || status >= 500;

/** What a screen says when the Worker could not be reached. Nothing the teacher did changed. */
export const COLLAB_UNREACHABLE = 'We could not reach Collab just now. Try again in a moment.';

export async function call<T>(
  getToken: GetToken,
  path: string,
  init: RequestInit = {},
): Promise<ApiResult<T>> {
  let token: string | null = null;
  try {
    token = await getToken();
  } catch {
    return { ok: false, status: 0, error: 'no_session' };
  }

  let response: Response;
  try {
    response = await fetch(path, {
      ...init,
      headers: {
        ...posthogHeaders(),
        ...(init.headers ?? {}),
        ...(token ? { authorization: `Bearer ${token}` } : {}),
      },
    });
  } catch {
    // A network blip. The caller keeps whatever the teacher typed or chose.
    return { ok: false, status: 0, error: 'network' };
  }

  const body = (await response.json().catch(() => ({}))) as Record<string, unknown>;

  if (!response.ok) {
    return {
      ok: false,
      status: response.status,
      error: typeof body.error === 'string' ? body.error : 'unknown',
      ...(typeof body.reason === 'string' ? { reason: body.reason } : {}),
    };
  }

  return { ok: true, data: body as T };
}

// --- groups and invitations ------------------------------------------------------------------

/**
 * The two writes the new-group flow is built out of, named once.
 *
 * They are thin on purpose — each is one `call` — and they are here rather than inlined in
 * three components because `NewGroupPage` performs both, `GroupRoom` performs the second, and
 * `StartedGroupPanel` retries the second. Three copies of a URL template is three places for
 * a path to drift, and the path is the thing the Worker matches on.
 *
 * **There is no endpoint for "create a group with invitations".** Creating the group and
 * creating each invitation are separate requests with no transaction across them, and the
 * caller — not this file — is what has to survive the group landing and an invitation not.
 */
export const createGroup = (
  getToken: GetToken,
  name: string,
): Promise<ApiResult<{ group: GroupSummary }>> =>
  call<{ group: GroupSummary }>(getToken, '/api/groups', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ name }),
  });

/** Admins only. The answer is the group as `GET /api/groups/:id` shows it. */
export const renameGroup = (
  getToken: GetToken,
  groupId: string,
  name: string,
): Promise<ApiResult<{ group: GroupSummary }>> =>
  call<{ group: GroupSummary }>(getToken, `/api/groups/${encodeURIComponent(groupId)}`, {
    method: 'PATCH',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ name }),
  });

export const createInvitation = (
  getToken: GetToken,
  groupId: string,
  email: string,
): Promise<ApiResult<InvitationOutcome>> =>
  call<InvitationOutcome>(
    getToken,
    `/api/groups/${encodeURIComponent(groupId)}/invitations`,
    {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ email }),
    },
  );

/** A new link for the same address, emailed when email is available. Revokes the old one. */
export const resendInvitation = (
  getToken: GetToken,
  groupId: string,
  invitationId: string,
): Promise<ApiResult<InvitationOutcome>> =>
  call<InvitationOutcome>(
    getToken,
    `/api/groups/${encodeURIComponent(groupId)}/invitations/${encodeURIComponent(invitationId)}/resend`,
    { method: 'POST', headers: { 'content-type': 'application/json' }, body: '{}' },
  );

// --- invitations addressed to me -------------------------------------------------------------

export const fetchMyInvitations = (getToken: GetToken): Promise<ApiResult<{ invitations: MyInvitation[] }>> =>
  call<{ invitations: MyInvitation[] }>(getToken, '/api/invitations/mine');

/** Join from the banner. Answers the group, in the shape `/api/invitations/accept` does. */
export const acceptInvitationById = (
  getToken: GetToken,
  invitationId: string,
): Promise<ApiResult<{ group: GroupSummary }>> =>
  call<{ group: GroupSummary }>(getToken, `/api/invitations/${encodeURIComponent(invitationId)}/accept`, {
    method: 'POST', headers: { 'content-type': 'application/json' }, body: '{}',
  });

export const declineInvitationById = (
  getToken: GetToken,
  invitationId: string,
): Promise<ApiResult<{ declined: true }>> =>
  call<{ declined: true }>(getToken, `/api/invitations/${encodeURIComponent(invitationId)}/decline`, {
    method: 'POST', headers: { 'content-type': 'application/json' }, body: '{}',
  });

/** Decline from the email's link. The token is the one the `/decline` fragment carried. */
export const declineInvitationByToken = (
  getToken: GetToken,
  token: string,
): Promise<ApiResult<{ declined: true }>> =>
  call<{ declined: true }>(getToken, '/api/invitations/decline', {
    method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ token }),
  });

// --- deleting, and the trash ---------------------------------------------------------------

/** A material in the trash: hidden from the group, its bytes kept until `restorableUntil`. */
export interface TrashedMaterial extends MaterialSummary {
  deletedAt: string;
  restorableUntil: string;
}

/**
 * Delete hides a material and keeps it restorable for thirty days. The room offers Undo on
 * the strength of the answer, so it is returned whole rather than reduced to ok.
 */
export const deleteMaterial = (
  getToken: GetToken,
  materialId: string,
): Promise<ApiResult<{ id: string; deletedAt: string; restorableUntil: string }>> =>
  call(getToken, `/api/materials/${encodeURIComponent(materialId)}`, { method: 'DELETE' });

/** A new title, for the uploader or an admin — the people delete allows. The file stays. */
export const renameMaterial = (
  getToken: GetToken,
  materialId: string,
  title: string,
): Promise<ApiResult<{ material: MaterialSummary }>> =>
  call(getToken, `/api/materials/${encodeURIComponent(materialId)}`, {
    method: 'PATCH',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ title }),
  });

/** Brings a trashed material back. 409 `group_full` when the group has no room for it. */
export const restoreMaterial = (
  getToken: GetToken,
  materialId: string,
): Promise<ApiResult<{ material: MaterialSummary }>> =>
  call(getToken, `/api/materials/${encodeURIComponent(materialId)}/restore`, { method: 'POST' });

/**
 * The thread has been read up to `newestSeen`, the `createdAt` of the newest comment that has
 * been on screen. 204 on success. The Worker clamps it to now and never moves it backwards,
 * so a stale tab or a skewed clock cannot unread anything or read the future.
 */
export const markRead = (
  getToken: GetToken,
  materialId: string,
  newestSeen: string,
): Promise<ApiResult<unknown>> =>
  call(getToken, `/api/materials/${encodeURIComponent(materialId)}/read`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ newestSeen }),
  });

/**
 * Save the group's Standards check defaults. An empty field is unset. The answer is the
 * context as stored, or null when no field is set.
 */
export const saveStandardsDefaults = (
  getToken: GetToken,
  groupId: string,
  ctx: StandardsContext,
): Promise<ApiResult<{ standardsDefaults: StandardsContext | null }>> =>
  call(getToken, `/api/groups/${encodeURIComponent(groupId)}/standards-defaults`, {
    method: 'PUT',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({
      jurisdiction: ctx.jurisdiction ?? null,
      academicSubject: ctx.academicSubject ?? null,
      grade: ctx.grade ?? null,
    }),
  });

/**
 * The caller's own shared standards default, already one grade, for the Standards check to
 * fill empty fields from (KTD11). Any failure is no default: the check opens as it did before.
 */
export const fetchTeacherDefault = async (getToken: GetToken): Promise<StandardsContext | null> => {
  const result = await call<{ standardsDefault: StandardsContext | null }>(getToken, '/api/standards-default');
  return result.ok ? result.data.standardsDefault ?? null : null;
};

/** "Use group default": clears the material's override for everyone in the group. */
export const clearStandardsOverride = (
  getToken: GetToken,
  materialId: string,
): Promise<ApiResult<{ standardsOverride: null }>> =>
  call(getToken, `/api/materials/${encodeURIComponent(materialId)}/standards-override`, {
    method: 'DELETE',
  });

/** "Delete for good". The bytes go, and nothing brings it back. */
export const purgeMaterial = (
  getToken: GetToken,
  materialId: string,
): Promise<ApiResult<{ id: string; purgedAt: string }>> =>
  call(getToken, `/api/materials/${encodeURIComponent(materialId)}/purge`, { method: 'DELETE' });

/** What this teacher may restore: their own, or everything if they administer the group. */
export const fetchTrash = (
  getToken: GetToken,
  groupId: string,
): Promise<ApiResult<{ materials: TrashedMaterial[] }>> =>
  call(getToken, `/api/groups/${encodeURIComponent(groupId)}/trash`);

// --- downloads -----------------------------------------------------------------------------

/**
 * How a fetched file reaches the teacher's disk.
 *
 * Injected so the test can assert the blob URL was created and revoked without a real anchor
 * click, and so this module has one impure line rather than several.
 */
export interface Saver {
  createObjectURL: (blob: Blob) => string;
  revokeObjectURL: (url: string) => void;
  click: (url: string, filename: string) => void;
}

export const domSaver: Saver = {
  createObjectURL: (blob) => URL.createObjectURL(blob),
  revokeObjectURL: (url) => { URL.revokeObjectURL(url); },
  click: (url, filename) => {
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = filename;
    anchor.rel = 'noopener';
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
  },
};

/**
 * Download a material (R12, R13).
 *
 * The bytes come back through `fetch` with the bearer token attached, exactly like every
 * other call, and the blob URL is revoked immediately after the click — it is a handle to
 * memory in this tab, not a link anyone can pass on.
 */
export async function downloadMaterial(
  getToken: GetToken,
  material: Pick<MaterialSummary, 'id' | 'filename'>,
  saver: Saver = domSaver,
): Promise<ApiResult<{ downloaded: true }>> {
  let token: string | null = null;
  try {
    token = await getToken();
  } catch {
    return { ok: false, status: 0, error: 'no_session' };
  }

  let response: Response;
  try {
    // The id is a path segment and there is no query string. Nothing about this URL is a
    // credential, which is what lets it appear in a log line or a replay without harm.
    response = await fetch(`/api/materials/${encodeURIComponent(material.id)}/content`, {
      headers: {
        ...posthogHeaders(),
        ...(token ? { authorization: `Bearer ${token}` } : {}),
      },
    });
  } catch {
    return { ok: false, status: 0, error: 'network' };
  }

  if (!response.ok) {
    const body = (await response.json().catch(() => ({}))) as Record<string, unknown>;
    return {
      ok: false,
      status: response.status,
      error: typeof body.error === 'string' ? body.error : 'unknown',
      ...(typeof body.reason === 'string' ? { reason: body.reason } : {}),
    };
  }

  const blob = await response.blob();
  const url = saver.createObjectURL(blob);
  try {
    saver.click(url, material.filename);
  } finally {
    saver.revokeObjectURL(url);
  }
  return { ok: true, data: { downloaded: true } };
}

// --- previews ------------------------------------------------------------------------------

/**
 * Just the two halves of `Saver` a viewer needs. `domSaver` satisfies it, so a test that
 * already injects a saver gets the preview's object URLs on the same fake.
 */
export type ObjectUrls = Pick<Saver, 'createObjectURL' | 'revokeObjectURL'>;

/**
 * What a previewable material comes back as, and the two are deliberately different shapes.
 *
 * An **image** becomes a `Blob`, because the only way to put authenticated bytes into an
 * `<img src>` is an object URL, and an object URL needs a Blob whose type is pinned here.
 *
 * A **PDF** comes back as raw bytes and never as a Blob, because pdf.js parses bytes and
 * nothing here ever builds a `blob:` URL for a PDF. That absence is the point: a PDF that is
 * never a URL is never something this origin — which holds the Clerk session and the
 * invitation token — can navigate to, frame, or hand to a plugin.
 */
export type MaterialPreview =
  | { kind: 'image'; blob: Blob }
  | { kind: 'pdf'; bytes: ArrayBuffer };

/**
 * Fetch a material's bytes for inline display (U11).
 *
 * Written as a sibling of `downloadMaterial` rather than a branch inside it, because the
 * only interesting thing about it is what it refuses to do:
 *
 * 1. **The bearer header, again, and no token in the URL.** An `<img src>` sends no headers
 *    at all, and the obvious repair — `?token=` — is the leak the comment at the top of this
 *    file exists to prevent. So the bytes come back through `fetch`, and an image reaches the
 *    DOM as an object URL the caller owns and revokes. A PDF reaches pdf.js as bytes and
 *    becomes no URL at all, for the same reason: given a URL, pdf.js would fetch the
 *    material itself, unauthenticated.
 * 2. **The blob's type comes from the allowlist, not from the response.** This is the load-
 *    bearing line. An object URL keeps the Blob's type and *loses every response header* —
 *    `nosniff`, the sandbox CSP and the disposition are all gone the moment the bytes become
 *    a `blob:` URL. So the type is chosen here, from `previewContentType` over the type the
 *    listing already showed, and a Worker that regressed into serving `text/html` cannot
 *    make this function build an HTML blob URL on the SPA's own origin.
 * 3. **A served type that disagrees with the allowlisted one is refused outright**, rather
 *    than relabelled. The two sides disagreeing about what a material is means one of them
 *    is wrong, and rendering it anyway is how the wrong one wins.
 */
export async function fetchMaterialPreview(
  getToken: GetToken,
  material: Pick<MaterialSummary, 'id' | 'contentType'>,
): Promise<ApiResult<MaterialPreview>> {
  const type = previewContentType(material.contentType);
  const kind = previewKindOfContentType(material.contentType);
  if (type === null || kind === null) {
    return { ok: false, status: 0, error: 'not_previewable', reason: 'not_previewable' };
  }

  let token: string | null = null;
  try {
    token = await getToken();
  } catch {
    return { ok: false, status: 0, error: 'no_session' };
  }

  let response: Response;
  try {
    // A path segment and nothing else — no query, no fragment, no credential.
    response = await fetch(`/api/materials/${encodeURIComponent(material.id)}/preview`, {
      headers: {
        ...posthogHeaders(),
        ...(token ? { authorization: `Bearer ${token}` } : {}),
      },
    });
  } catch {
    return { ok: false, status: 0, error: 'network' };
  }

  if (!response.ok) {
    const body = (await response.json().catch(() => ({}))) as Record<string, unknown>;
    return {
      ok: false,
      status: response.status,
      error: typeof body.error === 'string' ? body.error : 'unknown',
      ...(typeof body.reason === 'string' ? { reason: body.reason } : {}),
    };
  }

  const served = (response.headers.get('content-type') ?? '').split(';')[0]?.trim().toLowerCase();
  if (served !== type) {
    return { ok: false, status: response.status, error: 'not_previewable', reason: 'not_previewable' };
  }

  const bytes = await response.arrayBuffer();
  // A PDF stops here: pdf.js is handed these bytes, and nothing wraps them in a Blob whose
  // only use would be an object URL this viewer must not create.
  if (kind === 'pdf') return { ok: true, data: { kind, bytes } };
  return { ok: true, data: { kind, blob: new Blob([bytes], { type }) } };
}

// --- thumbnails -------------------------------------------------------------------------------

/**
 * A material's thumbnail, for a grid card. Null on any failure — a 404 is the ordinary "none
 * yet", and every other answer is drawn as the file-type icon too.
 *
 * Fetched with the bearer token like every other read, and handed back as a Blob whose type
 * is pinned to `image/jpeg` here rather than taken from the response, for the reason
 * `fetchMaterialPreview` gives: an object URL keeps the Blob's type and loses every header.
 * A served type that is not `image/jpeg` is refused rather than relabelled.
 */
export async function fetchThumbnail(
  getToken: GetToken,
  materialId: string,
): Promise<Blob | null> {
  try {
    const token = await getToken();
    const response = await fetch(`/api/materials/${encodeURIComponent(materialId)}/thumbnail`, {
      headers: {
        ...posthogHeaders(),
        ...(token ? { authorization: `Bearer ${token}` } : {}),
      },
    });
    if (!response.ok) return null;
    const served = (response.headers.get('content-type') ?? '').split(';')[0]?.trim().toLowerCase();
    if (served !== 'image/jpeg') return null;
    return new Blob([await response.arrayBuffer()], { type: 'image/jpeg' });
  } catch {
    return null;
  }
}

/**
 * Store a thumbnail the browser drew. True when the Worker kept it.
 *
 * The page count rides in the query — a display number, never a credential. The token is in
 * the header, as everywhere else. `fetch` sets `Content-Length` from the Blob, which the
 * Worker requires before it reads a byte.
 */
export async function putThumbnail(
  getToken: GetToken,
  materialId: string,
  blob: Blob,
  pageCount: number | null,
): Promise<boolean> {
  try {
    const token = await getToken();
    const query = pageCount !== null ? `?page_count=${encodeURIComponent(String(pageCount))}` : '';
    const response = await fetch(`/api/materials/${encodeURIComponent(materialId)}/thumbnail${query}`, {
      method: 'PUT',
      headers: {
        ...posthogHeaders(),
        'content-type': 'image/jpeg',
        ...(token ? { authorization: `Bearer ${token}` } : {}),
      },
      body: blob,
    });
    return response.ok;
  } catch {
    return false;
  }
}

// --- sharing -------------------------------------------------------------------------------

export interface UploadInput {
  file: File;
  title: string;
  note: string;
}

/** The multipart body both upload paths send. An empty note is left off. */
function buildMaterialForm(input: UploadInput): FormData {
  const form = new FormData();
  form.set('title', input.title);
  if (input.note.trim().length > 0) form.set('note', input.note);
  form.set('file', input.file);
  return form;
}

/**
 * The one `material_shared` event for a stored material. The Worker stores a canonical
 * content type, so this resolves; the file's own type is the fallback for a row written by
 * some later path. A format that cannot be read is left off rather than guessed — a wrong
 * label in a funnel is worse than a gap.
 */
function trackMaterialShared(
  track: (event: AnalyticsEvent) => void,
  groupId: string,
  material: MaterialSummary,
  file: File,
): void {
  const format = formatOfContentType(material.contentType) ?? formatOfContentType(file.type);
  if (format) {
    track(collabMaterialShared(groupId, material.id, sizeBucket(material.sizeBytes), format));
  }
}

/**
 * Share a material into a group (R11), and emit the one event that says so (KTD15).
 *
 * The event carries a size *bucket* and a format, never a filename, a title or a note. Those
 * are the teacher's words, and analytics is not where words go — `core/events.ts` takes none
 * of them, which is what makes that checkable by reading one file.
 */
export async function uploadMaterial(
  getToken: GetToken,
  groupId: string,
  input: UploadInput,
  track: (event: AnalyticsEvent) => void,
): Promise<ApiResult<{ material: MaterialSummary }>> {
  const form = buildMaterialForm(input);

  const result = await call<{ material: MaterialSummary }>(
    getToken,
    `/api/groups/${encodeURIComponent(groupId)}/materials`,
    { method: 'POST', body: form },
  );

  if (result.ok) trackMaterialShared(track, groupId, result.data.material, input.file);

  return result;
}

// --- sharing, with progress (the upload panel) ---------------------------------------------

/** What the panel's runner is told about one upload. Never throws. */
export type UploadOutcome =
  | { ok: true; material: MaterialSummary }
  | { ok: false; reason: string; status: number; retryAfter?: number };

export interface UploadProgressOptions {
  onProgress?: (sent: number, total: number) => void;
  /** The last byte left this tab; what follows is the Worker's inspection. */
  onSent?: () => void;
  signal?: AbortSignal;
}

/**
 * `Retry-After`, in seconds: either a number of seconds or an HTTP date. Anything unreadable
 * is `undefined`, and the caller picks its own wait.
 */
export function parseRetryAfter(value: string | null, now: number): number | undefined {
  if (value === null || value.trim() === '') return undefined;
  const trimmed = value.trim();
  if (/^\d+$/.test(trimmed)) return Number(trimmed);
  const at = Date.parse(trimmed);
  if (Number.isNaN(at)) return undefined;
  return Math.max(0, Math.ceil((at - now) / 1000));
}

/**
 * `uploadMaterial`, over `XMLHttpRequest` — `fetch` reports nothing while a body uploads, and
 * "N% of X MB" is the whole point of the panel.
 *
 * The same request in every other respect: the same multipart fields, the same bearer token
 * and PostHog headers, and the same one `material_shared` event on a success, fired here and
 * nowhere else (KTD15). A retry after a 429 is a second call, and only the call that succeeds
 * emits it, so a file is counted once however many times it was sent.
 */
export async function uploadWithProgress(
  getToken: GetToken,
  groupId: string,
  input: UploadInput,
  track: (event: AnalyticsEvent) => void,
  options: UploadProgressOptions = {},
): Promise<UploadOutcome> {
  if (options.signal?.aborted) return { ok: false, reason: 'cancelled', status: 0 };

  let token: string | null = null;
  try {
    token = await getToken();
  } catch {
    return { ok: false, reason: 'no_session', status: 0 };
  }

  const form = buildMaterialForm(input);

  const outcome = await new Promise<UploadOutcome>((resolve) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', `/api/groups/${encodeURIComponent(groupId)}/materials`);
    const headers: Record<string, string> = {
      ...posthogHeaders(),
      ...(token ? { authorization: `Bearer ${token}` } : {}),
    };
    for (const [name, value] of Object.entries(headers)) xhr.setRequestHeader(name, value);

    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable) options.onProgress?.(event.loaded, event.total);
    };
    xhr.upload.onload = () => { options.onSent?.(); };

    const abort = () => xhr.abort();
    options.signal?.addEventListener('abort', abort, { once: true });
    const settle = (result: UploadOutcome) => {
      options.signal?.removeEventListener('abort', abort);
      resolve(result);
    };

    xhr.onabort = () => settle({ ok: false, reason: 'cancelled', status: 0 });
    // A dropped connection. The caller says so in the row; nothing was stored.
    xhr.onerror = () => settle({ ok: false, reason: 'network', status: 0 });
    xhr.onload = () => {
      let body: Record<string, unknown> = {};
      try {
        body = JSON.parse(xhr.responseText) as Record<string, unknown>;
      } catch { /* an unreadable body reads as the status alone */ }

      if (xhr.status >= 200 && xhr.status < 300 && body.material) {
        settle({ ok: true, material: body.material as MaterialSummary });
        return;
      }
      const reason = typeof body.reason === 'string' ? body.reason
        : xhr.status === 404 ? 'not_found'
          : xhr.status === 429 ? 'rate_limited'
            : typeof body.error === 'string' ? body.error : 'unknown';
      const retryAfter = xhr.status === 429
        ? parseRetryAfter(xhr.getResponseHeader('retry-after'), Date.now())
        : undefined;
      settle({
        ok: false, reason, status: xhr.status,
        ...(retryAfter !== undefined ? { retryAfter } : {}),
      });
    };

    xhr.send(form);
  });

  if (outcome.ok) trackMaterialShared(track, groupId, outcome.material, input.file);
  return outcome;
}
